// Shared helper for rendering an AI interview prep guide as HTML.
// The guide payload can be either:
//   - a string (already-HTML from Quill, or plain text), or
//   - a structured object produced by the AI generator
//
// Used by:
//   - InterviewDetailDialog (admin Quill editor seed)
//   - PortalInterviews (client read-only view)
//
// Keep both call sites identical so what the client sees matches what staff
// authored. If you add a new section, update only this file.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GuideData = string | Record<string, any>

const escapeHtml = (s: unknown): string => {
  if (s === null || s === undefined) return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

// Returns true when the payload has at least one of the known guide
// sections. Used as a sanity check so unrecognized shapes fall through
// to the "guide is being prepared" empty state instead of rendering an
// empty Box.
export const isRecognizedGuide = (guide: unknown): boolean => {
  if (typeof guide === 'string') return guide.trim().length > 0
  if (!isPlainObject(guide)) return false
  return (
    !!guide.companyResearch ||
    (Array.isArray(guide.anticipatedQuestions) && guide.anticipatedQuestions.length > 0) ||
    (Array.isArray(guide.questionsToAsk) && guide.questionsToAsk.length > 0) ||
    (Array.isArray(guide.keyTalkingPoints) && guide.keyTalkingPoints.length > 0) ||
    (Array.isArray(guide.potentialChallenges) && guide.potentialChallenges.length > 0) ||
    !!guide.closingStrategy ||
    !!guide.followUpTips
  )
}

// Convert guide (string or object) to HTML. Returns '' for empty/unrecognized
// payloads — callers should check that and render an empty state instead of
// piping the empty string into innerHTML.
export const guideToHtml = (guide: GuideData | null | undefined): string => {
  if (!guide) return ''

  // String input — pass through unchanged for HTML, or wrap plain text in paragraphs.
  if (typeof guide === 'string') {
    if (guide.startsWith('<')) return guide
    return guide
      .split('\n\n')
      .map((paragraph) => {
        const lines = paragraph.split('\n').join('<br/>')
        return `<p>${lines}</p>`
      })
      .join('')
  }

  // Object input — render known sections in a consistent order. Any field
  // we don't recognize is silently ignored (never dumped as raw JSON).
  const sections: string[] = []

  const cr = (guide as Record<string, unknown>).companyResearch
  if (isPlainObject(cr)) {
    sections.push('<h2>Company Research</h2>')
    if (cr.overview) {
      sections.push(`<p><strong>Overview:</strong> ${escapeHtml(cr.overview)}</p>`)
    }
    if (cr.recentNews) {
      sections.push(`<p><strong>Recent News:</strong> ${escapeHtml(cr.recentNews)}</p>`)
    }
    if (cr.competitorsAndMarket) {
      sections.push(`<p><strong>Market Context:</strong> ${escapeHtml(cr.competitorsAndMarket)}</p>`)
    }
  }

  const aq = (guide as Record<string, unknown>).anticipatedQuestions
  if (Array.isArray(aq) && aq.length > 0) {
    sections.push('<h2>Anticipated Questions</h2>')
    aq.forEach((q, i) => {
      if (!isPlainObject(q)) return
      sections.push(`<h3>${i + 1}. ${escapeHtml(q.question)}</h3>`)
      if (q.strategy) {
        sections.push(`<p><strong>Strategy:</strong> ${escapeHtml(q.strategy)}</p>`)
      }
      if (q.sampleAnswer) {
        sections.push(`<p><strong>Sample Answer:</strong> ${escapeHtml(q.sampleAnswer)}</p>`)
      }
    })
  }

  const qta = (guide as Record<string, unknown>).questionsToAsk
  if (Array.isArray(qta) && qta.length > 0) {
    sections.push('<h2>Questions to Ask</h2>')
    sections.push('<ul>')
    qta.forEach((q) => {
      if (!isPlainObject(q)) return
      const question = escapeHtml(q.question)
      const purpose = q.purpose ? `<br/><em>${escapeHtml(q.purpose)}</em>` : ''
      sections.push(`<li><strong>${question}</strong>${purpose}</li>`)
    })
    sections.push('</ul>')
  }

  const ktp = (guide as Record<string, unknown>).keyTalkingPoints
  if (Array.isArray(ktp) && ktp.length > 0) {
    sections.push('<h2>Key Talking Points</h2>')
    sections.push('<ul>')
    ktp.forEach((point) => {
      sections.push(`<li>${escapeHtml(point)}</li>`)
    })
    sections.push('</ul>')
  }

  const pc = (guide as Record<string, unknown>).potentialChallenges
  if (Array.isArray(pc) && pc.length > 0) {
    sections.push('<h2>Potential Challenges</h2>')
    pc.forEach((c) => {
      if (!isPlainObject(c)) return
      if (c.challenge) {
        sections.push(`<p><strong>Challenge:</strong> ${escapeHtml(c.challenge)}</p>`)
      }
      if (c.strategy) {
        sections.push(`<p><strong>Strategy:</strong> ${escapeHtml(c.strategy)}</p>`)
      }
    })
  }

  const cs = (guide as Record<string, unknown>).closingStrategy
  if (cs) {
    sections.push('<h2>Closing Strategy</h2>')
    sections.push(`<p>${escapeHtml(cs)}</p>`)
  }

  const fut = (guide as Record<string, unknown>).followUpTips
  if (fut) {
    sections.push('<h2>Follow-up Tips</h2>')
    sections.push(`<p>${escapeHtml(fut)}</p>`)
  }

  return sections.join('')
}
